/**
 * Front-end model representing a document. See back-end complement for field descriptions.
 *
 * The following Field model attribute sets are located under the static `fields` attribute.
 * * Name
 * @module app/models/Document
 */
import Backbone from "backbone";
import Enums from "app/enums";
import _ from "underscore";
import FieldUtils from "app/utils/FieldUtils";
import auth from "app/auth";
import mime from "mime-types";
import session from "app/Session";
import urlService from "../UrlService";

var Document = Backbone.Model.extend(
  {
    _defaults: {
      Visibility: Enums.FileVisibility["Object"],
      Name: "File",
      FileName: "",
      MimeType: "",
      User: "",
      IsFolder: false,
    },

    urlRoot: function () {
      var self = this;

      return _.result(this.collection, "url", function () {
        return self._url || urlService.apiurl("/document");
      });
    },

    initialize: function (attributes, options) {
      Backbone.Model.prototype.initialize.apply(this, arguments);

      if (options && options.url) {
        this._url = options.url;
      } else {
        this._url = urlService.apiurl("/document");
      }

      this.options = options;
    },

    /**
     * Gets a fileName associated with the document model.
     * Uses the Name attribute when allowed. Else returns uploaded FileName
     * Two special case scenarios deal with legacy saving of the Name attribute.
     * If the name attribute already exists with the correct extension, return just it with appending a new one.
     * If the document was saved with an incorrect MimeType, use a mimetype from the original fileName
     * @return {String} A string representing the fileName of the document.
     */
    getFileName: function () {
      var documentName = this.get("Name");
      var mimeExtension = mime.extension(this.get("MimeType"));
      var originalMime = mime.lookup(this.get("FileName"));

      //Older files are saved with the extension already on the Name.
      //If the extension already exists on the Name as the same extension reflected by the MimeType, return the Name.
      if (
        mimeExtension &&
        mimeExtension == mime.extension(mime.lookup(documentName))
      ) {
        return documentName;
      } else if (
        mimeExtension &&
        this.get("MimeType") != "application/octet-stream"
      ) {
        //Application/Octect-stream is counted out because json files have been wrongly saved as application/octet-stream mimeType
        return documentName + "." + mimeExtension;
      } else if (originalMime) {
        return documentName + "." + mime.extension(originalMime);
      } else {
        return this.get("FileName");
      }
    },

    /**
     * Gets the API endpoint containing child documents of this document.
     * @return {String} The document endpoint.
     */
    getChildrenUrl: function () {
      return urlService.apiurl("/document/" + this.id + "/documents");
    },

    /**
     * Gets the link URL for the file stored under this document.
     * @param {String} token The authentication token for downloading the file.
     */
    getFilePath: function (token) {
      return urlService.apiurl(
        "/document/" +
          this.id +
          "/file/" +
          this.getFileName() +
          "?token=" +
          token
      );
    },
  },
  {
    fields: {
      Name: FieldUtils.fieldPrototype("Name", "Name", null, {
        sortable: false,
        filterable: false,
      }),
    },

    /**
     *
     * @static
     * @return {Object} A document factory, which has three functions: "createDocument", "createDocumentFileUrl", "createDocumentDownloadUrl". The first takes a hash of attributes, the second takes a document ID, and the third takes a document model. The first returns a Document instance, the second returns the file URL of a document (for uploading files), and the third returns the download URL of a document.
     */
    getFactory: function () {
      return {
        /**
         *
         * @param {Object} attrs
         */
        createDocument: function (attrs) {
          return new Document(
            _.defaults({}, attrs, {
              User: session.user.id,
            })
          );
        },

        /**
         *
         * @param id
         */
        createDocumentFileUrl: function (id) {
          return (
            urlService.apiBuilder
              .child("document")
              .child(id)
              .absolute()
              .url("/file") +
            "?token=" +
            auth.token
          );
        },

        /**
         * @todo why do we use "Name" here and "FileName" in the instance function?
         * @param document
         */
        createDocumentDownloadUrl: function (document) {
          return urlService.apiBuilder
            .child("document")
            .child(document.id)
            .child("file")
            .absolute()
            .url("/" + document.get("Name"));
        },
      };
    },
  }
);

export default Document;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/Document.js
// module id = 108
// module chunks = 1